import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSearchParams } from "react-router-dom";
import { CATEGORIES, STATUSES, CATEGORY_LIST, STATUS_LIST, TEMPERATURES, TEMPERATURE_LIST, fmtMoney } from "@/lib/leadMeta";
import { AddLeadDialog } from "@/components/AddLeadDialog";
import { BulkImportDialog } from "@/components/BulkImportDialog";
import { EditLeadDialog } from "@/components/EditLeadDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { SnapMenu } from "@/components/SnapMenu";
import { SnapPartnerDialog } from "@/components/SnapPartnerDialog";
import { AppointmentDialog } from "@/components/AppointmentDialog";
import { SnapSla } from "@/components/SnapSla";
import { LeadAgeDot } from "@/components/LeadAgeDot";
import { TypeLegend } from "@/components/TypeLegend";
import { formatPhone } from "@/lib/phone";
import { staticMapUrl, streetViewUrl } from "@/lib/googleMaps";
import { fmtDateTime } from "@/lib/sla";
import { Textarea } from "@/components/ui/textarea";
import { Search, Trash2, UserCheck, Share2, MapPin, Phone, Mail, Hand, Building2, Briefcase, Users, Clock, Eye, Pencil, Star, Send, MessageSquare, Printer, FileText, FileSpreadsheet, Download, ZoomIn, ZoomOut, ExternalLink, PhoneCall, ScrollText, Flame, Target, StickyNote, AtSign, History, RotateCcw, CheckCircle2, XCircle, Loader2, Upload, Zap, CalendarPlus, BookOpen } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { fileUrl, initials } from "@/lib/fileUrl";
import { dialHref } from "@/lib/dialer";
import { fillScript } from "@/lib/scriptTags";
import { exportExcel, exportPDF, printLeads } from "@/lib/leadExport";

const CatTag = ({ type, primary }) => {
  const c = CATEGORIES[type] || CATEGORIES.life;
  return <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium" style={{ backgroundColor: c.color + "1f", color: c.color }}>{primary && <Star className="h-3 w-3 fill-current" />}<c.icon className="h-3 w-3" /> {c.label}</span>;
};
const StatusPill = ({ status }) => {
  const s = STATUSES[status] || STATUSES.new;
  return <span className="inline-flex items-center gap-1.5 text-xs font-medium" style={{ color: s.color }}><span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} /> {s.label}</span>;
};
const TempPill = ({ t }) => {
  const x = TEMPERATURES[t];
  if (!x) return null;
  return <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium" style={{ backgroundColor: x.color + "1f", color: x.color }} data-testid={`temp-pill-${t}`}><Flame className="h-3 w-3" /> {x.label}</span>;
};
const Types = ({ lead }) => {
  const list = lead.insurance_types?.length ? lead.insurance_types : [lead.insurance_type].filter(Boolean);
  const primary = lead.primary_type || list[0];
  return <div className="flex flex-wrap gap-1">{list.map((t) => <CatTag key={t} type={t} primary={t === primary} />)}</div>;
};

function useNow(intervalMs = 30000) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), intervalMs); return () => clearInterval(t); }, [intervalMs]);
  return now;
}
const returnRemaining = (lead, now) => {
  if (lead.share_status !== "sent" || lead.share_opened || !lead.sent_at) return null;
  return new Date(lead.sent_at).getTime() + 24 * 3600 * 1000 - now;
};
const fmtRemaining = (ms) => {
  if (ms <= 0) return "returning…";
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};
const ReturnBadge = ({ lead, compact }) => {
  const now = useNow();
  const rem = returnRemaining(lead, now);
  if (rem == null) return null;
  const urgent = rem <= 3 * 3600 * 1000;
  return (
    <span data-testid={`return-timer-${lead.lead_id}`}
      className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${urgent ? "border-red-500/50 text-red-500" : "border-amber-500/40 text-amber-600 dark:text-amber-500"}`}>
      <Clock className="h-2.5 w-2.5" /> {compact ? fmtRemaining(rem) : `returns in ${fmtRemaining(rem)}`}
    </span>
  );
};

const OUTCOME_META = {
  won: { label: "Won", color: "#10b981", Icon: CheckCircle2 },
  lost: { label: "Lost", color: "#ef4444", Icon: XCircle },
  returned: { label: "Returned unopened (24h)", color: "#f59e0b", Icon: RotateCcw },
  resent: { label: "Re-sent elsewhere", color: "#6b7280", Icon: Send },
};
const SendHistory = ({ history }) => {
  if (!history || history.length === 0) return null;
  return (
    <div data-testid="send-history-section">
      <label className="flex items-center gap-1.5 text-xs font-medium uppercase text-muted-foreground"><History className="h-3.5 w-3.5" /> Send history</label>
      <div className="mt-2 space-y-2.5 rounded-lg border border-border p-3">
        {history.slice().reverse().map((h) => {
          const om = h.outcome ? OUTCOME_META[h.outcome] : null;
          return (
            <div key={h.id} className="flex items-start gap-2 text-xs" data-testid={`send-history-${h.id}`}>
              <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
              <div className="min-w-0">
                <p className="text-foreground">Sent to <span className="font-medium">{h.to_name}</span> <span className="text-muted-foreground">· {h.to_kind}</span> by {h.by_name}</p>
                <p className="text-muted-foreground">{fmtDateTime(h.sent_at)}</p>
                {h.opened_at && <p className="flex items-center gap-1 text-emerald-500"><Eye className="h-3 w-3" /> Opened · {fmtDateTime(h.opened_at)}</p>}
                {!h.opened_at && !h.outcome && <p className="text-amber-600 dark:text-amber-500">Awaiting open…</p>}
                {om && <p className="flex items-center gap-1 font-medium" style={{ color: om.color }}><om.Icon className="h-3 w-3" /> {om.label}{h.outcome_at ? ` · ${fmtDateTime(h.outcome_at)}` : ""}</p>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const BulkSendDialog = ({ leadIds, team, partners, me, onDone }) => {
  const [open, setOpen] = useState(false);
  const [sendTo, setSendTo] = useState("");
  const [busy, setBusy] = useState(false);
  const doSend = async () => {
    if (!sendTo) return toast.error("Pick a teammate or company to send to");
    const [kind, id] = sendTo.split(":");
    setBusy(true);
    try {
      const { data } = await api.post("/leads/bulk-send", { lead_ids: leadIds, ...(kind === "user" ? { to_user_id: id } : { to_org_id: id }) });
      toast.success(`${data.sent} lead(s) sent — 24h timer started${data.skipped ? ` · ${data.skipped} skipped` : ""}`);
      setOpen(false); setSendTo(""); onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="bulk-send-button"><Send className="mr-1 h-3.5 w-3.5" /> Send {leadIds.length}</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="bulk-send-dialog">
          <DialogHeader><DialogTitle className="font-heading">Send {leadIds.length} lead{leadIds.length !== 1 ? "s" : ""}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Each recipient must open a lead within 24 hours or it's automatically returned to you. You'll be notified on open, win or loss.</p>
            <Select value={sendTo} onValueChange={setSendTo}>
              <SelectTrigger data-testid="bulk-send-recipient-select"><SelectValue placeholder="Choose a teammate or company" /></SelectTrigger>
              <SelectContent>
                {team.filter((m) => m.user_id !== me.user_id).map((m) => <SelectItem key={`bu${m.user_id}`} value={`user:${m.user_id}`}>{m.name} · {m.role}</SelectItem>)}
                {partners.map((p) => <SelectItem key={`bo${p.other_org_id}`} value={`org:${p.other_org_id}`}>{p.other_org_name} (company)</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={doSend} disabled={busy || !sendTo} className="w-full" data-testid="bulk-send-confirm"><Send className="mr-2 h-4 w-4" /> Send {leadIds.length} lead{leadIds.length !== 1 ? "s" : ""}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

function LeadDetail({ lead, team, canManage, me, onClose, refresh, focusHistory, onRecordChange }) {
  useEffect(() => { api.post(`/new-leads/ack/${lead.lead_id}`).catch(() => {}); }, [lead.lead_id]);
  useEffect(() => {
    if (!focusHistory) return;
    const t = setTimeout(() => {
      const el = document.querySelector('[data-testid="send-history-section"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 400);
    return () => clearTimeout(t);
  }, [focusHistory]);
  const [busy, setBusy] = useState(false);
  const [bookedId, setBookedId] = useState(lead.book_entry_id || null);
  const [booking, setBooking] = useState(false);
  const addToBook = async () => {
    setBooking(true);
    try {
      const { data } = await api.post(`/leads/${lead.lead_id}/to-book`);
      setBookedId(data.entry_id);
      toast.success("Added to your Book of Business");
      refresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBooking(false); }
  };
  const [apptOpen, setApptOpen] = useState(false);
  const [zoom, setZoom] = useState(15);
  const locationImg = staticMapUrl(lead.lat, lead.lng, { zoom, color: (CATEGORIES[lead.primary_type]?.color || "#0D9488").replace("#", "") });
  const streetImg = streetViewUrl(lead.lat, lead.lng);
  const gmapsUrl = lead.lat != null && lead.lng != null ? `https://www.google.com/maps/search/?api=1&query=${lead.lat},${lead.lng}` : null;
  const [partners, setPartners] = useState([]);
  const [sharedOrgs, setSharedOrgs] = useState(lead.shared_org_ids || []);
  const canShareOrg = !lead.is_shared_in;
  const isCrossAgency = lead.is_shared_in || (lead.shared_out_count || 0) > 0;
  const [msgs, setMsgs] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [emailTo, setEmailTo] = useState("");
  const [emailMsg, setEmailMsg] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [savedContacts, setSavedContacts] = useState([]);
  const [scripts, setScripts] = useState([]);
  const [noteList, setNoteList] = useState(lead.notes_log || []);
  const [newNote, setNewNote] = useState("");
  const [showNotes, setShowNotes] = useState(false);
  const [activities, setActivities] = useState(lead.activities || []);
  const [closers, setClosers] = useState(null);
  const [showClosers, setShowClosers] = useState(false);
  const [mentions, setMentions] = useState([]);
  const [master, setMaster] = useState(null);
  const [inPool, setInPool] = useState(!!lead.master_pool);
  useEffect(() => { api.get("/master").then(({ data }) => setMaster(data)).catch(() => {}); }, []);

  useEffect(() => { api.get("/email-contacts").then(({ data }) => setSavedContacts(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/scripts").then(({ data }) => setScripts(data)).catch(() => {}); }, []);
  const saveContact = async () => {
    if (!emailTo.trim()) return toast.error("Enter an email first");
    try { const { data } = await api.post("/email-contacts", { email: emailTo.trim() }); setSavedContacts((p) => p.some((c) => c.contact_id === data.contact_id) ? p : [...p, data]); toast.success("Saved to contacts"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  useEffect(() => {
    if (!isCrossAgency) return;
    api.get(`/leads/${lead.lead_id}/messages`).then(({ data }) => setMsgs(data)).catch(() => {});
  }, [isCrossAgency, lead.lead_id]);

  const sendEmail = async () => {
    if (!emailTo.trim()) return toast.error("Enter a recipient email");
    setEmailBusy(true);
    try { await api.post(`/leads/${lead.lead_id}/email`, { to: emailTo.trim(), message: emailMsg }); toast.success("Email sent"); setEmailTo(""); setEmailMsg(""); setShowEmail(false); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setEmailBusy(false); }
  };
  const postMsg = async () => {
    const t = newMsg.trim(); if (!t) return;
    try { const { data } = await api.post(`/leads/${lead.lead_id}/messages`, { text: t }); setMsgs((p) => [...p, data]); setNewMsg(""); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  useEffect(() => {
    if (!canShareOrg) return;
    api.get("/partnerships").then(({ data }) => setPartners(data.filter((p) => p.status === "accepted"))).catch(() => {});
  }, [canShareOrg]);
  useEffect(() => { api.post(`/leads/${lead.lead_id}/mark-opened`).catch(() => {}); }, [lead.lead_id]);

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); refresh(); onClose(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const actWithUndo = async (fn, msg, undoFn) => {
    setBusy(true);
    try {
      await fn();
      toast.success(msg, {
        duration: 8000,
        action: {
          label: "Undo",
          onClick: async () => {
            try { await undoFn(); toast.success("Reverted"); refresh(); }
            catch (e) { toast.error("Couldn't undo that change"); }
          },
        },
      });
      if (onRecordChange) onRecordChange({ label: `${msg} · ${lead.name}`, undo: undoFn });
      refresh(); onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const changeStatus = (status) => {
    const prev = lead.status;
    if (status === prev) return;
    if (status === "closed_lost") {
      const reason = window.prompt("Why was this lead lost? A short reason is required.");
      if (!reason || !reason.trim()) { toast.error("A descriptive reason is required to mark a lead lost"); return; }
      actWithUndo(() => api.patch(`/leads/${lead.lead_id}`, { status, lost_reason: reason.trim() }), "Lead marked lost", () => api.patch(`/leads/${lead.lead_id}`, { status: prev }));
      return;
    }
    actWithUndo(() => api.patch(`/leads/${lead.lead_id}`, { status }), "Status updated", () => api.patch(`/leads/${lead.lead_id}`, { status: prev }));
  };
  const changeTemp = (temperature) => act(() => api.patch(`/leads/${lead.lead_id}`, { temperature }), "Temperature updated");
  const assign = (user_id) => {
    const prev = lead.assigned_to || null;
    const next = user_id === "none" ? null : user_id;
    if (next === prev) return;
    actWithUndo(() => api.post(`/leads/${lead.lead_id}/assign`, { user_id: next }), "Lead assigned", () => api.post(`/leads/${lead.lead_id}/assign`, { user_id: prev }));
  };
  const claim = () => act(() => api.post(`/leads/${lead.lead_id}/claim`), "Lead claimed");
  const remove = () => act(() => api.delete(`/leads/${lead.lead_id}`), "Lead deleted");

  const [shared, setShared] = useState(lead.shared_with || []);
  const [sendTo, setSendTo] = useState("");
  const doSend = () => {
    if (!sendTo) return toast.error("Pick a teammate or company to send to");
    const [kind, id] = sendTo.split(":");
    act(() => api.post(`/leads/${lead.lead_id}/send`, kind === "user" ? { to_user_id: id } : { to_org_id: id }), "Lead sent — 24h timer started");
  };
  const toggleShare = (id) => setShared((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const saveShare = () => act(() => api.post(`/leads/${lead.lead_id}/share`, { user_ids: shared }), "Sharing updated");

  const toggleOrg = (id) => setSharedOrgs((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const saveShareOrg = () => act(() => api.post(`/leads/${lead.lead_id}/share-org`, { org_ids: sharedOrgs }), "Partner sharing updated");

  const addMention = (m) => {
    setNewNote((t) => (t ? t.replace(/\s*$/, "") + " " : "") + "@" + m.name + " ");
    setMentions((p) => (p.some((x) => x.user_id === m.user_id) ? p : [...p, m]));
  };
  const addNote = async () => {
    const t = newNote.trim(); if (!t) return;
    const active = mentions.filter((m) => newNote.includes("@" + m.name)).map((m) => m.user_id);
    try { const { data } = await api.post(`/leads/${lead.lead_id}/notes`, { text: t, mentions: active }); setNoteList(data.notes_log || []); setActivities(data.activities || []); setNewNote(""); setMentions([]); toast.success(active.length ? "Note added · teammate notified" : "Note added"); refresh(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const loadClosers = async () => {
    setShowClosers((s) => !s);
    if (closers === null) {
      try { const { data } = await api.get(`/leads/${lead.lead_id}/closers`); setClosers(data); }
      catch { setClosers([]); }
    }
  };
  const shareToCloser = async (id) => {
    const ids = Array.from(new Set([...(shared || []), id]));
    try { await api.post(`/leads/${lead.lead_id}/share`, { user_ids: ids }); setShared(ids); toast.success("Lead shared with closer"); refresh(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const toggleMasterPool = async () => {
    const next = !inPool;
    try { await api.post(`/leads/${lead.lead_id}/share-master`, { in_pool: next }); setInPool(next); toast.success(next ? "Shared into the master pool" : "Removed from the master pool"); refresh(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="lead-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex flex-wrap items-center gap-2"><LeadAgeDot lead={lead} /> {lead.name} <Types lead={lead} />
            {lead.is_shared_in && <Badge variant="outline" className="border-amber-500/40 text-amber-500" data-testid="shared-in-badge"><Building2 className="mr-1 h-3 w-3" /> Shared by {lead.owner_org_name}</Badge>}
            {lead.share_status && <Badge variant="outline" className="border-primary/40 capitalize text-primary" data-testid="share-status-badge"><Send className="mr-1 h-3 w-3" /> {lead.share_status}{lead.sent_to_name ? ` · ${lead.sent_to_name}` : ""}</Badge>}
            <ReturnBadge lead={lead} />
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-2 text-sm">
            {lead.phone && (
              <div className="flex items-center justify-between gap-2">
                <p className="flex items-center gap-2 text-muted-foreground"><Phone className="h-4 w-4" /> {formatPhone(lead.phone)}</p>
                <a href={dialHref(lead.phone, me)} data-testid="lead-call-button" className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground transition-opacity hover:opacity-90"><PhoneCall className="h-3.5 w-3.5" /> Call</a>
              </div>
            )}
            {lead.email && <p className="flex items-center gap-2 text-muted-foreground"><Mail className="h-4 w-4" /> {lead.email}</p>}
            {lead.company && <p className="flex items-center gap-2 text-muted-foreground"><Building2 className="h-4 w-4" /> {lead.company}</p>}
            {lead.industry && <p className="flex items-center gap-2 text-muted-foreground" data-testid="lead-industry"><Briefcase className="h-4 w-4" /> {lead.industry}</p>}
            {lead.employees != null && lead.employees !== "" && <p className="flex items-center gap-2 text-muted-foreground" data-testid="lead-employees"><Users className="h-4 w-4" /> {lead.employees} employees</p>}
            <p className="flex items-center gap-2 text-muted-foreground" data-testid="lead-address"><MapPin className="h-4 w-4" /> {[lead.address, lead.address2, lead.city, lead.state, lead.zip].filter(Boolean).join(", ")}</p>
          </div>

          {(streetImg || locationImg) && (
            <div className="space-y-2" data-testid="lead-location-card">
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {streetImg && (
                  <div className="relative">
                    <img src={streetImg} alt="Street view of lead location" data-testid="lead-streetview-image"
                      className="h-40 w-full rounded-lg border border-border object-cover"
                      onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} />
                    <span className="absolute bottom-1 left-1 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">Street View</span>
                  </div>
                )}
                {locationImg && (
                  <div className="relative">
                    <img src={locationImg} alt="Lead location map" data-testid="lead-location-image"
                      className="h-40 w-full rounded-lg border border-border object-cover"
                      onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    <div className="absolute bottom-1 right-1 flex overflow-hidden rounded-md border border-white/20 bg-black/60 backdrop-blur">
                      <button type="button" data-testid="map-zoom-out" title="Zoom out" disabled={zoom <= 10}
                        onClick={() => setZoom((z) => Math.max(10, z - 1))}
                        className="px-1.5 py-1 text-white/90 transition-colors hover:bg-white/10 disabled:opacity-40"><ZoomOut className="h-3.5 w-3.5" /></button>
                      <button type="button" data-testid="map-zoom-in" title="Zoom in" disabled={zoom >= 20}
                        onClick={() => setZoom((z) => Math.min(20, z + 1))}
                        className="border-l border-white/20 px-1.5 py-1 text-white/90 transition-colors hover:bg-white/10 disabled:opacity-40"><ZoomIn className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                )}
              </div>
              {gmapsUrl && (
                <a href={gmapsUrl} target="_blank" rel="noreferrer" data-testid="open-google-maps-link"
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline">
                  <ExternalLink className="h-3.5 w-3.5" /> Open in Google Maps
                </a>
              )}
            </div>
          )}

          {lead.snapped_at && (
            <div className="rounded-lg border border-border px-4 py-3" data-testid="snap-info">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Snapped to <span className="font-medium text-foreground">{lead.assigned_to_name || "agent"}</span></span>
                <SnapSla lead={lead} showLabel />
              </div>
              <p className="mt-1 flex items-center gap-1.5 font-mono text-xs text-muted-foreground"><Clock className="h-3.5 w-3.5" /> {fmtDateTime(lead.snapped_at)}</p>
            </div>
          )}
          <div className="flex items-center justify-between rounded-lg border border-border px-4 py-3">
            <span className="text-sm text-muted-foreground">Premium estimate</span>
            <span className="font-mono font-semibold">{fmtMoney(lead.premium_estimate)}/yr</span>
          </div>
          {lead.commission_pct != null && lead.commission_pct !== "" && (
            <div className="flex items-center justify-between rounded-lg border border-primary/30 bg-primary/5 px-4 py-3" data-testid="lead-detail-commission">
              <span className="text-sm text-muted-foreground">Projected commission <span className="text-xs">({lead.commission_pct}%)</span></span>
              <span className="font-mono font-semibold text-primary">{fmtMoney((Number(lead.premium_estimate) || 0) * (Number(lead.commission_pct) || 0) / 100)}/yr</span>
            </div>
          )}
          {lead.status === "closed_won" && (
            bookedId ? (
              <div className="flex items-center justify-center gap-2 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 text-sm font-medium text-emerald-600" data-testid="lead-in-book">
                <BookOpen className="h-4 w-4" /> In your Book of Business
              </div>
            ) : (
              <Button onClick={addToBook} disabled={booking} className="w-full" data-testid="add-to-book-button">
                {booking ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <BookOpen className="mr-2 h-4 w-4" />} Add to Book of Business
              </Button>
            )
          )}
          {lead.notes && <p className="rounded-lg bg-muted px-4 py-3 text-sm">{lead.notes}</p>}

          <div className="rounded-lg border border-border p-3" data-testid="notes-section">
            <button type="button" onClick={() => setShowNotes((s) => !s)} className="flex w-full items-center justify-between text-sm font-medium" data-testid="toggle-notes">
              <span className="flex items-center gap-2"><StickyNote className="h-4 w-4" /> Notes ({noteList.length})</span>
              <span className="text-xs text-muted-foreground">{showNotes ? "Hide" : "Open"}</span>
            </button>
            {showNotes && (
              <div className="mt-3 space-y-3">
                <div className="max-h-48 space-y-2 overflow-y-auto">
                  {noteList.length === 0 && <p className="text-xs text-muted-foreground">No notes yet — add the first one below.</p>}
                  {noteList.slice().reverse().map((n) => (
                    <div key={n.note_id} className="rounded-md bg-muted px-3 py-2 text-sm" data-testid={`note-${n.note_id}`}>
                      <p className="whitespace-pre-wrap">{n.text}</p>
                      <p className="mt-1 text-[11px] text-muted-foreground">{n.author_name} · {fmtDateTime(n.ts)}</p>
                    </div>
                  ))}
                </div>
                <div className="space-y-2">
                  <Textarea rows={2} value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Add a note… use Mention to tag a teammate" data-testid="note-input" />
                  <div className="flex items-center justify-between gap-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="outline" data-testid="mention-trigger"><AtSign className="mr-1 h-3.5 w-3.5" /> Mention</Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="max-h-56 overflow-y-auto">
                        {team.filter((m) => m.user_id !== me.user_id).map((m) => (
                          <DropdownMenuItem key={m.user_id} onClick={() => addMention(m)} data-testid={`mention-${m.user_id}`}>{m.name} <span className="ml-1 text-xs text-muted-foreground">· {m.role}</span></DropdownMenuItem>
                        ))}
                        {team.filter((m) => m.user_id !== me.user_id).length === 0 && <DropdownMenuItem disabled>No teammates</DropdownMenuItem>}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <Button size="sm" onClick={addNote} data-testid="add-note-button"><Send className="mr-1 h-4 w-4" /> Add note</Button>
                  </div>
                  {mentions.filter((m) => newNote.includes("@" + m.name)).length > 0 && (
                    <p className="text-xs text-muted-foreground" data-testid="mention-preview">Notifying: {mentions.filter((m) => newNote.includes("@" + m.name)).map((m) => "@" + m.name).join(", ")}</p>
                  )}
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="text-xs font-medium uppercase text-muted-foreground">Status</label>
            <Select value={lead.status} onValueChange={changeStatus}>
              <SelectTrigger className="mt-1.5" data-testid="detail-status-select" disabled={busy}><SelectValue /></SelectTrigger>
              <SelectContent>{STATUS_LIST.map((k) => <SelectItem key={k} value={k}>{STATUSES[k].label}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-xs font-medium uppercase text-muted-foreground">Lead temperature</label>
            <Select value={lead.temperature || ""} onValueChange={changeTemp}>
              <SelectTrigger className="mt-1.5" data-testid="detail-temperature-select" disabled={busy}><SelectValue placeholder="Set temperature" /></SelectTrigger>
              <SelectContent>{TEMPERATURE_LIST.map((k) => <SelectItem key={k} value={k}>{TEMPERATURES[k].label}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div className="flex flex-wrap gap-2" data-testid="lead-quick-actions">
            <Button variant="outline" size="sm" onClick={() => setApptOpen(true)} data-testid="book-appointment-button">
              <CalendarPlus className="mr-1.5 h-4 w-4" /> Book appointment
            </Button>
          </div>

          {canManage && (
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-3" data-testid="snap-section">
              <p className="text-xs font-semibold uppercase text-primary">SNAP assign</p>
              <p className="mt-1 text-xs text-muted-foreground">Lock this lead exclusively to one agent, or snap it to a connected partner agency & notify them instantly.</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <SnapMenu leadId={lead.lead_id} team={team.filter((m) => m.user_id !== me.user_id)} onDone={() => { refresh(); onClose(); }} label="SNAP to agent" />
                {partners.length > 0 && !lead.is_shared_in && (
                  <SnapPartnerDialog leadId={lead.lead_id} partners={partners} onDone={() => { refresh(); onClose(); }} />
                )}
              </div>
            </div>
          )}

          {canManage ? (
            <div>
              <label className="text-xs font-medium uppercase text-muted-foreground">Assigned agent</label>
              <Select value={lead.assigned_to || "none"} onValueChange={assign}>
                <SelectTrigger className="mt-1.5" data-testid="detail-assign-select" disabled={busy}><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {team.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.name} ({m.role})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="flex items-center justify-between rounded-lg border border-border px-4 py-3">
              <span className="text-sm text-muted-foreground">{lead.assigned_to_name ? `Assigned to ${lead.assigned_to_name}` : "Unassigned"}</span>
              {(!lead.assigned_to || lead.assigned_to === me.user_id) && (
                <Button size="sm" variant="secondary" onClick={claim} disabled={busy} data-testid="claim-lead-button"><Hand className="mr-2 h-4 w-4" /> Claim</Button>
              )}
            </div>
          )}

          <div>
            <label className="text-xs font-medium uppercase text-muted-foreground">Share with teammates</label>
            <div className="mt-2 space-y-1.5 rounded-lg border border-border p-3" data-testid="share-list">
              {team.filter((m) => m.user_id !== me.user_id).map((m) => (
                <label key={m.user_id} className="flex cursor-pointer items-center gap-2.5 text-sm">
                  <Checkbox checked={shared.includes(m.user_id)} onCheckedChange={() => toggleShare(m.user_id)} data-testid={`share-checkbox-${m.user_id}`} />
                  {m.name} <span className="text-xs text-muted-foreground">· {m.role}</span>
                </label>
              ))}
              {team.length <= 1 && <p className="text-xs text-muted-foreground">No teammates to share with yet.</p>}
            </div>
            <Button size="sm" variant="outline" className="mt-2" onClick={saveShare} disabled={busy} data-testid="save-share-button"><Share2 className="mr-2 h-4 w-4" /> Update sharing</Button>
          </div>

          <div data-testid="send-lead-section">
            <label className="text-xs font-medium uppercase text-muted-foreground">Send lead (24h timer)</label>
            <p className="mt-1 text-xs text-muted-foreground">The recipient must open it within 24 hours or it's returned to you. You'll be notified when they open, win or lose it.</p>
            <div className="mt-2 flex items-center gap-2">
              <Select value={sendTo} onValueChange={setSendTo}>
                <SelectTrigger className="flex-1" data-testid="send-recipient-select"><SelectValue placeholder="Choose a teammate or company" /></SelectTrigger>
                <SelectContent>
                  {team.filter((m) => m.user_id !== me.user_id).map((m) => <SelectItem key={`u${m.user_id}`} value={`user:${m.user_id}`}>{m.name} · {m.role}</SelectItem>)}
                  {partners.map((p) => <SelectItem key={`o${p.other_org_id}`} value={`org:${p.other_org_id}`}>{p.other_org_name} (company)</SelectItem>)}
                </SelectContent>
              </Select>
              <Button size="sm" onClick={doSend} disabled={busy || !sendTo} data-testid="send-lead-button"><Send className="mr-1 h-4 w-4" /> Send</Button>
            </div>
          </div>

          {master?.in_master && !lead.is_shared_in && (
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-3" data-testid="master-pool-section">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="flex items-center gap-2 text-sm font-semibold text-primary"><Building2 className="h-4 w-4" /> Master pool · {master.master_name}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{inPool ? "This lead is shared with all companies in the master account." : `Share this lead with the ${master.member_count} member(s) of your master account.`}</p>
                </div>
                <Button size="sm" variant={inPool ? "outline" : "default"} onClick={toggleMasterPool} disabled={busy} data-testid="toggle-master-pool">{inPool ? "Remove from pool" : "Share to master pool"}</Button>
              </div>
            </div>
          )}
          {lead.is_shared_in && lead.master_pool && (
            <div className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-xs text-primary" data-testid="master-pool-badge"><Building2 className="mr-1 inline h-3 w-3" /> Shared via master pool by {lead.owner_org_name}</div>
          )}

          <div className="rounded-lg border border-primary/30 bg-primary/5 p-3" data-testid="closers-section">
            <button type="button" onClick={loadClosers} className="flex w-full items-center justify-between text-sm font-semibold text-primary" data-testid="find-closers-button">
              <span className="flex items-center gap-2"><Target className="h-4 w-4" /> Find closers for this lead</span>
              <span className="text-xs">{showClosers ? "Hide" : "Show"}</span>
            </button>
            {showClosers && (
              <div className="mt-3 space-y-2">
                {closers === null && <p className="text-xs text-muted-foreground">Loading closers…</p>}
                {closers && closers.length === 0 && <p className="text-xs text-muted-foreground">No active closers match this lead's coverage types.</p>}
                {closers && closers.map((c) => (
                  <div key={c.user_id} className="flex items-center gap-2.5 rounded-md border border-border bg-background p-2.5" data-testid={`closer-${c.user_id}`}>
                    <Avatar className="h-9 w-9 shrink-0">
                      {c.picture && <AvatarImage src={fileUrl(c.picture)} alt="" />}
                      <AvatarFallback className="text-xs">{initials(c.name)}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{c.name} <span className="text-xs text-muted-foreground">· {c.role}</span></p>
                      <div className="mt-0.5 flex flex-wrap gap-1">{(c.lead_preferences || []).map((t) => <span key={t} className="rounded-full px-1.5 py-0.5 text-[10px]" style={{ backgroundColor: (CATEGORIES[t]?.color || "#0D9488") + "22", color: CATEGORIES[t]?.color || "#0D9488" }}>{CATEGORIES[t]?.label || t}</span>)}</div>
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                      {c.bio_active && c.bio && (
                        <Popover>
                          <PopoverTrigger asChild>
                            <button type="button" data-testid={`closer-bio-toggle-${c.user_id}`} className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-1 text-xs text-muted-foreground transition-colors hover:text-foreground"><Eye className="h-3 w-3" /> Bio</button>
                          </PopoverTrigger>
                          <PopoverContent align="end" className="w-72" data-testid={`closer-bio-${c.user_id}`}>
                            <div className="flex items-center gap-3">
                              <Avatar className="h-11 w-11">
                                {c.picture && <AvatarImage src={fileUrl(c.picture)} alt="" />}
                                <AvatarFallback>{initials(c.name)}</AvatarFallback>
                              </Avatar>
                              <div className="min-w-0">
                                <p className="font-heading font-semibold">{c.name}</p>
                                <p className="text-xs capitalize text-muted-foreground">{c.role} · Closer</p>
                              </div>
                            </div>
                            <p className="mt-3 whitespace-pre-wrap text-sm text-muted-foreground">{c.bio}</p>
                            {c.phone && <a href={dialHref(c.phone, me)} className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground"><PhoneCall className="h-3.5 w-3.5" /> {formatPhone(c.phone)}</a>}
                          </PopoverContent>
                        </Popover>
                      )}
                      {c.phone && <a href={dialHref(c.phone, me)} data-testid={`closer-call-${c.user_id}`} className="inline-flex items-center gap-1 rounded-full bg-primary px-2.5 py-1 text-xs font-semibold text-primary-foreground"><PhoneCall className="h-3 w-3" /> Call</a>}
                      <Button size="sm" variant="outline" onClick={() => shareToCloser(c.user_id)} data-testid={`closer-share-${c.user_id}`}><Share2 className="mr-1 h-3 w-3" /> Send</Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {canShareOrg && partners.length > 0 && (
            <div data-testid="share-org-section">
              <label className="text-xs font-medium uppercase text-muted-foreground">Share with partner agencies</label>
              <div className="mt-2 space-y-1.5 rounded-lg border border-border p-3" data-testid="share-org-list">
                {partners.map((p) => (
                  <label key={p.other_org_id} className="flex cursor-pointer items-center gap-2.5 text-sm">
                    <Checkbox checked={sharedOrgs.includes(p.other_org_id)} onCheckedChange={() => toggleOrg(p.other_org_id)} data-testid={`share-org-checkbox-${p.other_org_id}`} />
                    <Building2 className="h-3.5 w-3.5 text-muted-foreground" /> {p.other_org_name}
                  </label>
                ))}
              </div>
              <Button size="sm" variant="outline" className="mt-2" onClick={saveShareOrg} disabled={busy} data-testid="save-share-org-button"><Building2 className="mr-2 h-4 w-4" /> Update partner sharing</Button>
            </div>
          )}

          <div className="rounded-lg border border-border p-3" data-testid="email-lead-section">
            <button type="button" onClick={() => setShowEmail((s) => !s)} className="flex w-full items-center justify-between text-sm font-medium" data-testid="toggle-email-lead">
              <span className="flex items-center gap-2"><Mail className="h-4 w-4" /> Email this lead's info</span>
              <span className="text-xs text-muted-foreground">{showEmail ? "Hide" : "Send"}</span>
            </button>
            {showEmail && (
              <div className="mt-3 space-y-2">
                {(team.filter((m) => m.active !== false && m.email && m.user_id !== me.user_id).length > 0 || savedContacts.length > 0) && (
                  <Select onValueChange={(v) => setEmailTo(v)}>
                    <SelectTrigger data-testid="email-recipient-select"><SelectValue placeholder="Choose a teammate or saved contact" /></SelectTrigger>
                    <SelectContent>
                      {team.filter((m) => m.active !== false && m.email && m.user_id !== me.user_id).map((m) => (
                        <SelectItem key={`tm-${m.user_id}`} value={m.email} data-testid={`email-teammate-${m.user_id}`}>{m.name} — {m.email} · teammate</SelectItem>
                      ))}
                      {savedContacts.map((c) => (
                        <SelectItem key={`sc-${c.contact_id}`} value={c.email}>{c.name ? `${c.name} — ${c.email}` : c.email} · saved</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <div className="flex gap-2">
                  <Input type="email" placeholder="…or enter an email address" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} data-testid="email-to-input" className="flex-1" />
                  <Button size="sm" variant="outline" onClick={saveContact} data-testid="save-contact-button">Save</Button>
                </div>
                <Textarea rows={2} placeholder="Optional note…" value={emailMsg} onChange={(e) => setEmailMsg(e.target.value)} data-testid="email-message-input" />
                <Button size="sm" onClick={sendEmail} disabled={emailBusy} data-testid="send-email-lead-button"><Send className="mr-2 h-4 w-4" /> Send email</Button>
              </div>
            )}
          </div>

          {scripts.length > 0 && (
            <div className="rounded-lg border border-border p-3" data-testid="lead-scripts-section">
              <p className="flex items-center gap-2 text-xs font-semibold uppercase text-muted-foreground"><ScrollText className="h-3.5 w-3.5" /> Call scripts</p>
              <div className="mt-2 space-y-2">
                {scripts.map((s) => (
                  <details key={s.script_id} className="rounded-md border border-border/60 bg-muted/40" data-testid={`lead-script-${s.script_id}`}>
                    <summary className="cursor-pointer px-3 py-2 text-sm font-medium">{s.title}</summary>
                    <p className="whitespace-pre-wrap px-3 pb-3 text-sm text-muted-foreground">{fillScript(s.body, lead, me)}</p>
                  </details>
                ))}
              </div>
            </div>
          )}

          {isCrossAgency && (
            <div data-testid="lead-inbox-section">
              <label className="flex items-center gap-1.5 text-xs font-medium uppercase text-muted-foreground"><MessageSquare className="h-3.5 w-3.5" /> Partner chat</label>
              <div className="mt-2 max-h-52 space-y-2 overflow-y-auto rounded-lg border border-border p-3" data-testid="lead-messages">
                {msgs.length === 0 && <p className="text-xs text-muted-foreground">No messages yet. Start the conversation with the partner agency.</p>}
                {msgs.map((m) => (
                  <div key={m.msg_id} className="text-xs">
                    <p className="text-foreground">{m.text}</p>
                    <p className="text-muted-foreground">{m.user_name} · {m.org_name} · {fmtDateTime(m.ts)}</p>
                  </div>
                ))}
              </div>
              <div className="mt-2 flex gap-2">
                <Input value={newMsg} onChange={(e) => setNewMsg(e.target.value)} placeholder="Write a message…" onKeyDown={(e) => { if (e.key === "Enter") postMsg(); }} data-testid="lead-message-input" />
                <Button size="sm" onClick={postMsg} data-testid="send-message-button"><Send className="h-4 w-4" /></Button>
              </div>
            </div>
          )}

          <SendHistory history={lead.send_history} />

          <div>
            <label className="text-xs font-medium uppercase text-muted-foreground">Activity log</label>
            <div className="mt-2 max-h-52 space-y-2 overflow-y-auto rounded-lg border border-border p-3" data-testid="activity-log">
              {activities.slice().reverse().map((a, i) => (
                <div key={i} className="flex items-start gap-2 text-xs">
                  <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                  <div>
                    <p className="text-foreground">{a.detail || a.action}</p>
                    <p className="text-muted-foreground">{a.actor_name} · {fmtDateTime(a.ts)}</p>
                  </div>
                </div>
              ))}
              {activities.length === 0 && <p className="text-xs text-muted-foreground">No activity yet.</p>}
            </div>
          </div>

          {canManage && (
            <Button variant="destructive" className="w-full" onClick={remove} disabled={busy} data-testid="delete-lead-button"><Trash2 className="mr-2 h-4 w-4" /> Delete lead</Button>
          )}
        </div>
        <AppointmentDialog open={apptOpen} onClose={() => setApptOpen(false)} leadId={lead.lead_id} leadName={lead.name} />
      </DialogContent>
    </Dialog>
  );
}

const ExportMenu = ({ leads, brand, selectedCount }) => {
  const run = async (fn) => { if (!leads.length) return toast.error("No leads to export"); await fn(leads, brand); };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" data-testid="export-menu-trigger"><Download className="mr-2 h-4 w-4" /> Export{selectedCount > 0 ? ` (${selectedCount})` : ""}</Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => run(exportPDF)} data-testid="export-pdf"><FileText className="mr-2 h-4 w-4" /> Download PDF</DropdownMenuItem>
        <DropdownMenuItem onClick={() => run(exportExcel)} data-testid="export-excel"><FileSpreadsheet className="mr-2 h-4 w-4" /> Download Excel</DropdownMenuItem>
        <DropdownMenuItem onClick={() => run(printLeads)} data-testid="export-print"><Printer className="mr-2 h-4 w-4" /> Print</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const RecentChangesPanel = ({ items, onUndo, onClear }) => {
  if (!items.length) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" data-testid="recent-changes-trigger"><History className="mr-1.5 h-3.5 w-3.5" /> Recent <span className="ml-1 rounded-full bg-primary/15 px-1.5 text-[10px] font-bold text-primary">{items.length}</span></Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="recent-changes-panel">
        <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
          <span className="font-heading text-sm font-semibold">Recent changes</span>
          <button onClick={onClear} className="text-xs text-muted-foreground underline hover:text-foreground" data-testid="recent-changes-clear">Clear</button>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {items.map((c) => (
            <div key={c.id} className="flex items-center justify-between gap-2 border-b border-border/60 px-4 py-2.5" data-testid={`recent-change-${c.id}`}>
              <div className="min-w-0">
                <p className="truncate text-sm">{c.label}</p>
                <p className="text-xs text-muted-foreground">{new Date(c.ts).toLocaleTimeString()}</p>
              </div>
              <Button size="sm" variant="ghost" className="shrink-0 text-primary" onClick={() => onUndo(c)} data-testid={`recent-undo-${c.id}`}><RotateCcw className="mr-1 h-3.5 w-3.5" /> Undo</Button>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default function Leads() {
  const { user } = useAuth();
  const canManage = user.role === "admin" || user.role === "manager";
  const [leads, setLeads] = useState(null);
  const [org, setOrg] = useState(null);
  const [team, setTeam] = useState([]);
  const [selected, setSelected] = useState(null);
  const [editLead, setEditLead] = useState(null);
  const [filters, setFilters] = useState({ insurance_types: [], status: "all", q: "", masterPool: false, masterAll: false });
  const [sortBy, setSortBy] = useState("newest");
  const [ageBucket, setAgeBucket] = useState("all");
  const [selectedIds, setSelectedIds] = useState([]);
  const [master, setMaster] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [partners, setPartners] = useState([]);
  const [confirmDel, setConfirmDel] = useState(null);
  const [delBusy, setDelBusy] = useState(false);
  const [focusHistory, setFocusHistory] = useState(false);
  const [recent, setRecent] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const importId = searchParams.get("import");
  const importLabel = searchParams.get("label");
  const clearImportFilter = () => setSearchParams({}, { replace: true });
  const recordChange = useCallback((c) => {
    setRecent((p) => [{ id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, ts: Date.now(), ...c }, ...p].slice(0, 20));
  }, []);
  const undoChange = async (c) => {
    try { await c.undo(); toast.success("Reverted"); setRecent((p) => p.filter((x) => x.id !== c.id)); load(); }
    catch (e) { toast.error("Couldn't undo that change"); }
  };
  const toggleSel = (id) => setSelectedIds((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  useEffect(() => { api.get("/org").then(({ data }) => setOrg(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/partnerships").then(({ data }) => setPartners(data.filter((p) => p.status === "accepted"))).catch(() => {}); }, []);
  useEffect(() => { api.get("/master").then(({ data }) => setMaster(data)).catch(() => {}); }, []);
  useEffect(() => { if (master?.in_master && (user.role === "admin" || user.role === "manager")) api.get("/master/pipeline").then(({ data }) => setPipeline(data)).catch(() => {}); }, [master, user.role]);
  const bulkPool = async () => {
    try { const { data } = await api.post("/leads/bulk-share-master", { lead_ids: selectedIds, in_pool: true }); toast.success(`${data.updated} lead(s) shared into the master pool`); setSelectedIds([]); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const batchAssign = async (uid) => {
    const ids = shown.map((l) => l.lead_id);
    if (!ids.length) return;
    try {
      const { data } = await api.post("/leads/bulk-assign", { lead_ids: ids, user_id: uid });
      const who = team.find((m) => m.user_id === uid);
      toast.success(`${data.assigned} lead${data.assigned !== 1 ? "s" : ""} assigned to ${who?.name || "agent"}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const batchSnap = async () => {
    const ids = shown.map((l) => l.lead_id);
    if (!ids.length) return;
    if (!window.confirm(`SNAP all ${ids.length} lead(s) round-robin across your active agents?`)) return;
    try {
      const { data } = await api.post("/leads/bulk-snap", { lead_ids: ids });
      toast.success(`${data.snapped} lead${data.snapped !== 1 ? "s" : ""} snapped across ${data.agents} agent${data.agents !== 1 ? "s" : ""}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const bulkDelete = () => {
    const leadsToDel = (leads || []).filter((l) => selectedIds.includes(l.lead_id));
    setConfirmDel({ ids: [...selectedIds], leads: leadsToDel });
  };
  const restoreDeleted = async (snapshots) => {
    try {
      const { data } = await api.post("/leads/bulk-restore", { snapshots });
      toast.success(`${data.restored} lead${data.restored !== 1 ? "s" : ""} restored`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const doConfirmedDelete = async () => {
    if (!confirmDel) return;
    setDelBusy(true);
    try {
      const { data } = await api.post("/leads/bulk-delete", { lead_ids: confirmDel.ids });
      const parts = [];
      if (data.deleted) parts.push(`${data.deleted} deleted`);
      if (data.removed) parts.push(`${data.removed} removed from your agency`);
      const snaps = data.snapshots || [];
      toast.success(parts.length ? parts.join(" · ") : "No leads deleted", snaps.length ? {
        duration: 8000,
        action: { label: "Undo", onClick: () => restoreDeleted(snaps) },
      } : undefined);
      if (snaps.length) recordChange({ label: `Deleted ${confirmDel.ids.length} lead${confirmDel.ids.length !== 1 ? "s" : ""}`, undo: () => restoreDeleted(snaps) });
      setSelectedIds((p) => p.filter((id) => !confirmDel.ids.includes(id)));
      setConfirmDel(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setDelBusy(false); }
  };
  const brand = { name: org?.name, color: org?.brand_color, logoUrl: org?.logo ? fileUrl(org.logo) : null, address: org?.address || "" };

  const load = useCallback(async () => {
    const params = {};
    if (filters.insurance_types.length) params.insurance_type = filters.insurance_types;
    if (filters.status !== "all") params.status = filters.status;
    if (filters.q) params.q = filters.q;
    if (filters.masterPool) params.master_pool = true;
    if (filters.masterAll) params.master_all = true;
    if (importId) params.import_id = importId;
    const { data } = await api.get("/leads", {
      params,
      paramsSerializer: (p) => new URLSearchParams(
        Object.entries(p).flatMap(([k, v]) => Array.isArray(v) ? v.map((x) => [k, x]) : [[k, v]])
      ).toString(),
    });
    setLeads(data);
  }, [filters, importId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/team").then(({ data }) => setTeam(data)).catch(() => {}); }, []);

  const focusLeadId = searchParams.get("lead");
  useEffect(() => {
    if (!focusLeadId) return;
    let cancelled = false;
    const wantHistory = searchParams.get("focus") === "history";
    api.get(`/leads/${focusLeadId}`).then(({ data }) => {
      if (cancelled) return;
      setSelected(data);
      setFocusHistory(wantHistory);
      setSearchParams({}, { replace: true });
    }).catch(() => { if (!cancelled) setSearchParams({}, { replace: true }); });
    return () => { cancelled = true; };
  }, [focusLeadId]); // eslint-disable-line react-hooks/exhaustive-deps

  const removeLead = (l) => setConfirmDel({ ids: [l.lead_id], leads: [l] });

  const hoursSince = (iso) => (iso ? (Date.now() - new Date(iso).getTime()) / 3600000 : 0);
  const shown = (leads || [])
    .filter((l) => {
      if (ageBucket === "all") return true;
      const h = hoursSince(l.created_at);
      if (ageBucket === "fresh") return h < 24;
      if (ageBucket === "aging") return h >= 24 && h <= 72;
      return h > 72;
    })
    .sort((a, b) => sortBy === "newest"
      ? new Date(b.created_at) - new Date(a.created_at)
      : new Date(a.created_at) - new Date(b.created_at));

  const shownIds = shown.map((l) => l.lead_id);
  const allSelected = shown.length > 0 && shownIds.every((id) => selectedIds.includes(id));
  const toggleAll = () => setSelectedIds(allSelected ? [] : shownIds);
  const exportLeads = selectedIds.length ? shown.filter((l) => selectedIds.includes(l.lead_id)) : shown;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold sm:text-3xl">Leads</h1>
          <p className="mt-1 text-sm text-muted-foreground">{leads ? `${shown.length} lead${shown.length !== 1 ? "s" : ""} in view` : "Loading…"}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {selectedIds.length > 0 && (
            <button onClick={() => setSelectedIds([])} className="text-xs text-muted-foreground underline hover:text-foreground" data-testid="clear-selection">{selectedIds.length} selected · Clear</button>
          )}
          {selectedIds.length > 0 && (
            <BulkSendDialog leadIds={selectedIds} team={team} partners={partners} me={user} onDone={() => { setSelectedIds([]); load(); }} />
          )}
          {selectedIds.length > 0 && master?.in_master && (
            <Button size="sm" variant="outline" onClick={bulkPool} data-testid="bulk-master-pool"><Building2 className="mr-1 h-3.5 w-3.5" /> Add {selectedIds.length} to master pool</Button>
          )}
          {selectedIds.length > 0 && canManage && (
            <Button size="sm" variant="outline" onClick={bulkDelete} data-testid="bulk-delete-button" className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"><Trash2 className="mr-1 h-3.5 w-3.5" /> Delete {selectedIds.length}</Button>
          )}
          <RecentChangesPanel items={recent} onUndo={undoChange} onClear={() => setRecent([])} />
          <ExportMenu leads={exportLeads} brand={brand} selectedCount={selectedIds.length} />
          <BulkImportDialog onDone={load} />
          <AddLeadDialog onDone={load} />
        </div>
      </div>

      {importId && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-primary/40 bg-primary/5 px-4 py-2.5" data-testid="import-filter-banner">
          <span className="flex items-center gap-2 text-sm text-primary"><Upload className="h-4 w-4" /> Showing leads from import{importLabel ? `: ${importLabel}` : ""} · {shown.length} lead{shown.length !== 1 ? "s" : ""}</span>
          <div className="flex flex-wrap items-center gap-2">
            {canManage && shown.length > 0 && (
              <>
                <Select onValueChange={batchAssign}>
                  <SelectTrigger className="h-8 w-40" data-testid="batch-assign-select"><SelectValue placeholder="Assign all to…" /></SelectTrigger>
                  <SelectContent>
                    {team.filter((m) => m.active !== false).map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.name} · {m.role}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button size="sm" variant="outline" onClick={batchSnap} data-testid="batch-snap-button"><Zap className="mr-1 h-3.5 w-3.5" /> SNAP all</Button>
              </>
            )}
            <button onClick={clearImportFilter} className="text-xs font-medium text-muted-foreground underline hover:text-foreground" data-testid="clear-import-filter">Clear filter</button>
          </div>
        </div>
      )}

      {pipeline?.in_master && pipeline.by_company?.length > 0 && (
        <Card className="p-5" data-testid="master-pipeline">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="flex items-center gap-2 font-heading font-semibold"><Building2 className="h-4 w-4 text-primary" /> {pipeline.master_name} — combined pipeline</h3>
            <span className="text-sm text-muted-foreground">{pipeline.totals.leads} leads · ${Math.round(pipeline.totals.premium).toLocaleString()} premium</span>
          </div>
          <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {pipeline.by_company.map((c) => (
              <div key={c.org_id} className="rounded-lg border border-border p-3" data-testid={`pipeline-company-${c.org_id}`}>
                <p className="font-medium">{c.name}</p>
                <p className="text-xs text-muted-foreground">{c.count} leads · {c.won} won · ${Math.round(c.premium).toLocaleString()} premium</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-[200px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search name, email, phone…" value={filters.q} data-testid="lead-search-input"
              onChange={(e) => setFilters({ ...filters, q: e.target.value })} className="pl-9" />
          </div>
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger className="w-[150px]" data-testid="leads-filter-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUS_LIST.map((k) => <SelectItem key={k} value={k}>{STATUSES[k].label}</SelectItem>)}
            </SelectContent>
          </Select>
          {master?.in_master && (
            <Button variant={filters.masterPool ? "default" : "outline"} onClick={() => setFilters((f) => ({ ...f, masterPool: !f.masterPool, masterAll: false }))} data-testid="filter-master-pool"><Building2 className="mr-1 h-4 w-4" /> Master pool</Button>
          )}
          {master?.in_master && (user.role === "admin" || user.role === "manager") && (
            <Button variant={filters.masterAll ? "default" : "outline"} onClick={() => setFilters((f) => ({ ...f, masterAll: !f.masterAll, masterPool: false }))} data-testid="filter-master-all"><Building2 className="mr-1 h-4 w-4" /> All member leads</Button>
          )}
          <Select value={ageBucket} onValueChange={setAgeBucket}>
            <SelectTrigger className="w-[150px]" data-testid="leads-age-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any age</SelectItem>
              <SelectItem value="fresh">Fresh (&lt;24h)</SelectItem>
              <SelectItem value="aging">Aging (24–72h)</SelectItem>
              <SelectItem value="stale">Stale (&gt;72h)</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[150px]" data-testid="leads-sort"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest first</SelectItem>
              <SelectItem value="oldest">Oldest first</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="leads-type-chips">
          {CATEGORY_LIST.map((k) => {
            const active = filters.insurance_types.includes(k);
            const c = CATEGORIES[k];
            return (
              <button key={k} type="button" data-testid={`leads-type-chip-${k}`}
                onClick={() => setFilters((f) => ({ ...f, insurance_types: active ? f.insurance_types.filter((t) => t !== k) : [...f.insurance_types, k] }))}
                className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors"
                style={{ backgroundColor: c.color + (active ? "33" : "1a"), color: c.color, borderColor: c.color + (active ? "" : "55") }}>
                <c.icon className="h-3.5 w-3.5" /> {c.label}
              </button>
            );
          })}
          {filters.insurance_types.length > 0 && (
            <button type="button" onClick={() => setFilters((f) => ({ ...f, insurance_types: [] }))}
              className="text-xs text-muted-foreground underline hover:text-foreground" data-testid="leads-type-clear">Clear</button>
          )}
        </div>
        <TypeLegend className="mt-3 border-t border-border/60 pt-3" active={filters.insurance_types}
          onSelect={(k) => setFilters((f) => ({ ...f, insurance_types: f.insurance_types.length === 1 && f.insurance_types[0] === k ? [] : [k] }))} />
      </Card>

      <Card className="overflow-hidden">
        {!leads ? (
          <div className="space-y-2 p-4">{[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
        ) : shown.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground" data-testid="leads-empty">No leads match your filters.</div>
        ) : (
          <>
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full text-sm" data-testid="leads-table">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase text-muted-foreground">
                  <th className="w-10 px-4 py-3 font-medium"><Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="select-all-leads" aria-label="Select all" /></th>
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Product</th>
                  <th className="px-4 py-3 font-medium">Location</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Assigned</th>
                  <th className="px-4 py-3 text-right font-medium">Premium</th>
                  <th className="px-4 py-3 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((l) => (
                  <tr key={l.lead_id} onClick={() => setSelected(l)} data-testid={`lead-row-${l.lead_id}`}
                    className="cursor-pointer border-b border-border/60 transition-colors hover:bg-accent/50">
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <Checkbox checked={selectedIds.includes(l.lead_id)} onCheckedChange={() => toggleSel(l.lead_id)} data-testid={`select-lead-${l.lead_id}`} />
                    </td>
                    <td className="px-4 py-3">
                      <p className="flex items-center gap-2 font-medium text-foreground"><LeadAgeDot lead={l} /> {l.name}{l.unread_count > 0 && <span data-testid={`unread-badge-${l.lead_id}`} className="inline-flex items-center gap-1 rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-bold text-primary-foreground"><MessageSquare className="h-2.5 w-2.5" /> {l.unread_count}</span>}</p>
                      <p className="text-xs text-muted-foreground">{l.phone ? formatPhone(l.phone) : l.email}</p>
                      {l.is_shared_in && <span className="mt-1 inline-flex items-center gap-1 rounded-full border border-amber-500/40 px-1.5 py-0.5 text-[10px] font-medium text-amber-500" data-testid={`shared-in-tag-${l.lead_id}`}><Building2 className="h-2.5 w-2.5" /> {l.owner_org_name}</span>}
                      <ReturnBadge lead={l} compact />
                    </td>
                    <td className="px-4 py-3"><Types lead={l} /></td>
                    <td className="px-4 py-3 text-muted-foreground">{l.city}, {l.state} <span className="font-mono text-xs">{l.zip}</span></td>
                    <td className="px-4 py-3"><StatusPill status={l.status} /></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {l.assigned_to_name
                          ? <span className="inline-flex items-center gap-1 text-xs"><UserCheck className="h-3.5 w-3.5 text-primary" /> {l.assigned_to_name}</span>
                          : <Badge variant="outline" className="text-xs">Unassigned</Badge>}
                        <SnapSla lead={l} />
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-mono">{fmtMoney(l.premium_estimate)}</td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-0.5">
                        <Button size="icon" variant="ghost" title="View" onClick={() => setSelected(l)} data-testid={`view-lead-${l.lead_id}`}><Eye className="h-4 w-4" /></Button>
                        {l.phone && <Button asChild size="icon" variant="ghost" title="Call"><a href={dialHref(l.phone, user)} data-testid={`call-lead-${l.lead_id}`}><PhoneCall className="h-4 w-4" /></a></Button>}
                        <Button size="icon" variant="ghost" title="Print" onClick={() => printLeads([l])} data-testid={`print-lead-${l.lead_id}`}><Printer className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" title="Edit" onClick={() => setEditLead(l)} data-testid={`edit-lead-${l.lead_id}`}><Pencil className="h-4 w-4" /></Button>
                        {canManage && <SnapMenu leadId={l.lead_id} team={team.filter((m) => m.user_id !== user.user_id)} onDone={load} size="icon" variant="ghost" label="" />}
                        {canManage && <Button size="icon" variant="ghost" title="Delete" onClick={() => removeLead(l)} data-testid={`delete-lead-btn-${l.lead_id}`} className="text-destructive hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="divide-y divide-border/60 md:hidden" data-testid="leads-cards">
            {shown.map((l) => (
              <div key={l.lead_id} className="p-4" data-testid={`lead-card-${l.lead_id}`} onClick={() => setSelected(l)}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="flex items-center gap-2 truncate font-medium text-foreground"><LeadAgeDot lead={l} /> {l.name}{l.unread_count > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-bold text-primary-foreground"><MessageSquare className="h-2.5 w-2.5" /> {l.unread_count}</span>}</p>
                    <p className="truncate text-xs text-muted-foreground">{l.phone ? formatPhone(l.phone) : l.email}</p>
                    {l.is_shared_in && <span className="mt-1 inline-flex items-center gap-1 rounded-full border border-amber-500/40 px-1.5 py-0.5 text-[10px] font-medium text-amber-500"><Building2 className="h-2.5 w-2.5" /> {l.owner_org_name}</span>}
                  </div>
                  <span className="shrink-0 font-mono text-sm">{fmtMoney(l.premium_estimate)}</span>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Types lead={l} />
                  <StatusPill status={l.status} />
                  <SnapSla lead={l} />
                  <ReturnBadge lead={l} compact />
                </div>
                <p className="mt-2 text-xs text-muted-foreground">{l.city}, {l.state} {l.zip} · {l.assigned_to_name || "Unassigned"}</p>
                <div className="mt-3 flex flex-wrap items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                  <Button size="sm" variant="outline" onClick={() => setSelected(l)} data-testid={`view-lead-m-${l.lead_id}`}><Eye className="mr-1.5 h-4 w-4" /> View</Button>
                  {l.phone && <Button asChild size="sm" variant="outline"><a href={dialHref(l.phone, user)} data-testid={`call-lead-m-${l.lead_id}`}><PhoneCall className="mr-1.5 h-4 w-4" /> Call</a></Button>}
                  <Button size="sm" variant="outline" onClick={() => printLeads([l])} data-testid={`print-lead-m-${l.lead_id}`}><Printer className="mr-1.5 h-4 w-4" /> Print</Button>
                  <Button size="sm" variant="outline" onClick={() => setEditLead(l)} data-testid={`edit-lead-m-${l.lead_id}`}><Pencil className="mr-1.5 h-4 w-4" /> Edit</Button>
                  {canManage && <SnapMenu leadId={l.lead_id} team={team.filter((m) => m.user_id !== user.user_id)} onDone={load} />}
                  {canManage && <Button size="sm" variant="outline" onClick={() => removeLead(l)} data-testid={`delete-lead-m-${l.lead_id}`} className="text-destructive hover:text-destructive"><Trash2 className="mr-1.5 h-4 w-4" /> Delete</Button>}
                </div>
              </div>
            ))}
          </div>
          </>
        )}
      </Card>

      {selected && (
        <LeadDetail lead={selected} team={team} canManage={canManage} me={user} focusHistory={focusHistory} onRecordChange={recordChange}
          onClose={() => { setSelected(null); setFocusHistory(false); }} refresh={load} />
      )}

      <Dialog open={!!confirmDel} onOpenChange={(o) => { if (!o) setConfirmDel(null); }}>
        <DialogContent data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading text-destructive"><Trash2 className="h-5 w-5" /> Delete {confirmDel?.ids.length} lead{confirmDel?.ids.length !== 1 ? "s" : ""}?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">This can't be undone. {confirmDel?.ids.length !== 1 ? "These leads" : "This lead"} will be permanently removed{confirmDel?.leads?.some((l) => l.is_shared_in) ? " (shared-in leads only lose your agency's access)" : ""}:</p>
          <div className="max-h-52 space-y-1.5 overflow-y-auto rounded-lg border border-border p-3">
            {confirmDel?.leads?.map((l) => (
              <div key={l.lead_id} className="flex items-center justify-between gap-2 text-sm" data-testid={`delete-confirm-item-${l.lead_id}`}>
                <span className="truncate font-medium text-foreground">{l.name}</span>
                <span className="shrink-0 text-xs text-muted-foreground">{[l.city, l.state].filter(Boolean).join(", ")}{l.is_shared_in ? " · shared-in" : ""}</span>
              </div>
            ))}
            {confirmDel && (!confirmDel.leads || confirmDel.leads.length === 0) && <p className="text-xs text-muted-foreground">{confirmDel.ids.length} selected lead(s)</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDel(null)} disabled={delBusy} data-testid="delete-confirm-cancel">Cancel</Button>
            <Button variant="destructive" onClick={doConfirmedDelete} disabled={delBusy} data-testid="delete-confirm-button">{delBusy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Delete {confirmDel?.ids.length}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {editLead && <EditLeadDialog lead={editLead} onClose={() => setEditLead(null)} onDone={load} />}
    </div>
  );
}
